import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { getUrlParamsFetch, handleAxiosError, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  AlertSeverityEnum,
  Cliente,
  Contrato,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';



const URLS_POST_ACTIVACIONES_RADIUS = {
  SET:`${urlBackend}api/v1/set-datos-completos-radius/`,
  STOP:`${urlBackend}api/v1/suspender-servicio-radius/`,
  REACTIVATE:`${urlBackend}api/v1/reactivar-servicio-radius/`,
}
type ActivacioneRadius = {
  type_request: "SET"|"STOP"|"REACTIVATE",
  pppoe: string,
  pppassword?: string,
  ipv4_actual?: string,
  ipv4?: string,
}

export type SingleRadiusObject = {
  attribute: string,
  id: number,
  op: string,
  username?: string,
  value: string
  cliente?: Partial<Cliente>
  contrato?: Partial<Contrato>

  // page?: number,
  // per_page?: number,
  // total?: number
}

export type RadiusObjects = {
  data: SingleRadiusObject[]
  username?:string
  page?: number,
  per_page?: number,
  total?: number
}
export type IRadCheckPagingRes = {
  data: SingleRadiusObject[],
  username?:string
  page: number,
  per_page: number,
  total: number
}




export interface IRadiusObjectsParams extends Partial<RadiusObjects> {
  page?: number
  per_page?: number
}


export type postActivacionesRadiusParams = ActivacioneRadius;

const { post, get } = erpApi();



export const useFetchGetRadiusCheck = (
  { params, enabled }: {
    params?: IRadiusObjectsParams,
    enabled: boolean
  }
) => {
  return useQuery({
    queryKey: ['rad-check', params || {}],
    queryFn: async () => {
      return await getRadiusCheck(params);
    },
    enabled,
  });
};

export const getRadiusCheck = async (params?: IRadiusObjectsParams) => {
  const queryParams = getUrlParamsFetch(params || {})
  const resp = await get<any>(`${urlBackend}api/v1/get-radcheck/?${queryParams}`,true);
  // console.log({resp})
  return resp;
};



export const usePostActivacionesRadius = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: POST_ACTIVACIONES_RADIUS,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activaciones-radius'] });
      queryClient.invalidateQueries({ queryKey: ['rad-check'] });
      queryClient.invalidateQueries({ queryKey: ['rad-activity'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Configuración realizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};



//estado de cliente en radius
export const usePostEstadoRadiusCliente = ({
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: getEstadoRadiusCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estado-cliente-bras'] });

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Información de cliente en radius obtenida correctamente.',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};



export const POST_ACTIVACIONES_RADIUS = async (data: postActivacionesRadiusParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  switch(data.type_request){
    case "SET":
      return post<ActivacioneRadius>(URLS_POST_ACTIVACIONES_RADIUS.SET, data, true);
    case "STOP":
      return post<ActivacioneRadius>(URLS_POST_ACTIVACIONES_RADIUS.STOP, data, true);
    case "REACTIVATE":
      return post<ActivacioneRadius>(URLS_POST_ACTIVACIONES_RADIUS.REACTIVATE, data, true);
    default:
      throw new Error("Invalid type request")
  }

};


export const useFetchGetRadAct = (
  { params, enabled }: {
    params?: IRadiusObjectsParams,
    enabled: boolean
  }
) => {
  return useQuery({
    queryKey: ['rad-activity', ...Object.values(params || {})],
    //     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['cajas-primarias-red'] });

    queryFn: async () => {
      return await getRadiusActivity(params);
    },
    retry: (attempt, error) => attempt < 2 && (!isAxiosError(error) || !error.response || error.response.status >= 500),
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000),
    enabled,
    throwOnError: false
  });
};

export const getRadiusActivity = async (params?: IRadiusObjectsParams) => {
  const queryParams = getUrlParamsFetch(params || {})
  const resp = await get<any>(`${urlBackend}api/v1/get-radacct/?${queryParams}`,true);
  // console.log({resp})
  return resp;
};


// =============================================================================
// RAD REPLY DATA
// =============================================================================
export const getRadiusRadReply = async (params?: IRadiusObjectsParams) => {
  const queryParams = getUrlParamsFetch(params || {})
  const resp = await get<any>(`${urlBackend}api/v1/get-radreply/?${queryParams}`,true);
  // console.log({resp})
  return resp;
};

// =============================================================================
// ESTADO DE CLIENTE EN RADIUS
// =============================================================================
export interface IRadiusStatus {
  status: "Offline"|"Online",
  server_ip: string | null,
  username: string | null,
  ip_address: string | null,
  mac_address: string | null,
  raw_details:string | null
}
export const getEstadoRadiusCliente = async (params?: {pppoe:string, server_id:string}) => {
  const resp = await post<IRadiusStatus>(`${urlBackend}api/v1/radius-status/`,params,true);
  return resp;
};




// export const useUpdateCajaPrimaria = ({
//   navigate,

//   enableRedirect = true,
//   returnUrl,

//   returnErrorUrl,
//   enableErrorRedirect = false,
//   customMessageErrorAlert,

//   enableSuccessAlert = true,
//   customSuccessMessage,
// }: UseMutationsParams = {}) => {
//   const queryClient = useQueryClient();
//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
//   const setAlert = useUiStore.getState().setAlert;

//   return useMutation({
//     mutationFn: updateCajaPrimaria,
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['cajas-primarias-red'] });

//       enableRedirect && navigate && returnUrl && navigate(returnUrl);

//       enableSuccessAlert &&
//         setAlert &&
//         setAlert(
//           true,
//           customSuccessMessage ?? 'Manga actualizada correctamente',
//           AlertSeverityEnum.success
//         );
//     },
//     onError: error => {
//       enableErrorRedirect &&
//         navigate &&
//         returnUrl &&
//         navigate(returnErrorUrl ?? (returnUrl || ''));

//       handleAxiosError(error, customMessageErrorAlert);
//     },
//     onSettled: () => {
//       setIsGlobalLoading(false);
//     },
//   });
// };

// export const useDeleteCajaPrimaria = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: deleteCajaPrimaria,
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['cajas-primarias-red'] });
//     },
//     onError: () => { },
//   });
// };



// export const getCajaPrimarias = async (params?: GetCajaPrimariasParams) => {
//   const stateParams = { ...params };

//   // filterByState
//   // if (stateParams.filterByState === false && stateParams.estado === undefined) {
//   //   delete stateParams.estado;
//   // } else if (stateParams.filterByState !== false) {
//   //   stateParams.estado = true;
//   // }
//   // delete stateParams.filterByState;

//   const queryParams = getUrlParamsFetch(stateParams);
//   return get<CajaPrimariaPagingRes>(`${urlBackend}api/v1/cajaprimaria/?${queryParams}`, true);
// };

// export const getCajaPrimaria = async (id: string) => {
//   try {
//     return await get<CajaPrimaria>(`${urlBackend}api/v1/cajaprimaria/${id}`, true);
//   } catch (error) {
//     handleAxiosError(error);
//   }
// };



// export const updateCajaPrimaria = async ({ id, data }: UpdateCajaPrimariaParams) => {
//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
//   setIsGlobalLoading(true);

//   return put<CajaPrimaria>(`${urlBackend}api/v1/cajaprimaria/${id}/`, data, true);
// };

// export const deleteCajaPrimaria = async (id: string) => {
//   const setAlert = useUiStore.getState().setAlert;

//   const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

//   setIsGlobalLoading(true);
//   const res = await remove<CajaPrimaria>(`${urlBackend}api/v1/cajaprimaria/${id}/`, true);

//   setAlert(true, res.message, res.status);
//   return res;
// };
